import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import MatchChat from "@/components/MatchChat";
import ProfileModal from "@/components/ProfileModal";
import {
  getMatches, getRequests, respondRequest, markNotificationsRead,
  api, getMatchProfile,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Heart, MessageCircle, UserPlus, Check, X, Users, RefreshCw, Info,
  MapPin, Phone, Loader2,
} from "lucide-react";

// Defensive utility: coerce whatever the API returns into a usable
// array of objects with `match_id` and `user_id`. Bad rows are logged
// once and dropped, so a single malformed entry can never white-screen
// the page.
function sanitizeMatches(raw, label) {
  if (!raw) return [];
  let arr = raw;
  if (!Array.isArray(arr)) {
    // Some endpoints wrap the list; accept either shape.
    if (Array.isArray(arr.matches)) arr = arr.matches;
    else if (Array.isArray(arr.data)) arr = arr.data;
    else {
      console.error(`[Matches] ${label}: expected array, got`, raw);
      return [];
    }
  }
  const bad = [];
  const good = [];
  for (const item of arr) {
    if (!item || typeof item !== "object" || !item.match_id || !item.user_id) {
      bad.push(item);
    } else {
      good.push(item);
    }
  }
  if (bad.length) {
    console.error(`[Matches] ${label}: dropped ${bad.length} invalid entries`, bad);
  }
  return good;
}

function sanitizeRequests(raw, label) {
  if (!Array.isArray(raw)) {
    console.error(`[Matches] ${label}: expected array, got`, raw);
    return [];
  }
  const bad = [];
  const good = [];
  for (const item of raw) {
    if (!item || typeof item !== "object" || !item.request_id) {
      bad.push(item);
    } else {
      good.push(item);
    }
  }
  if (bad.length) {
    console.error(`[Matches] ${label}: dropped ${bad.length} invalid entries`, bad);
  }
  return good;
}

export default function Matches() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChat, setActiveChat] = useState(null);
  const [showRequests, setShowRequests] = useState(false);
  const [requests, setRequests] = useState({ dating: [], friend: [] });
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);

  // userId -> phone number (from /whatsapp/contacts)
  const [phones, setPhones] = useState({});

  useEffect(() => {
    if (!user) { setMatches([]); setLoading(false); return; }
    Promise.all([loadMatches(), loadRequests(), loadPhones()])
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadMatches = async () => {
    if (!user) return;
    setError(null);
    try {
      const [datingRaw, friendsRaw] = await Promise.all([
        getMatches("dating").catch((e) => {
          console.error("[Matches] dating fetch failed:", e);
          return [];
        }),
        getMatches("friendship").catch((e) => {
          console.error("[Matches] friendship fetch failed:", e);
          return [];
        }),
      ]);

      const dating = sanitizeMatches(datingRaw, "dating").map((m) => ({
        ...m,
        match_type: "dating",
      }));
      const friends = sanitizeMatches(friendsRaw, "friendship").map((m) => ({
        ...m,
        match_type: "friendship",
      }));

      const combined = [...dating, ...friends];

      // Sort by created_at desc. Bad timestamps fall to the end instead
      // of poisoning the comparator with NaN.
      combined.sort((a, b) => {
        const at = Date.parse(a.created_at || "");
        const bt = Date.parse(b.created_at || "");
        const av = Number.isNaN(at) ? 0 : at;
        const bv = Number.isNaN(bt) ? 0 : bt;
        return bv - av;
      });

      setMatches(combined);
      setUnreadCount(
        combined.reduce((acc, m) => acc + (Number(m.unread_count) || 0), 0)
      );
    } catch (err) {
      console.error("[Matches] loadMatches failed:", err);
      setError("Could not load matches. Please try again.");
    }
  };

  const loadRequests = async () => {
    if (!user) return;
    setRequestsLoading(true);
    try {
      const data = await getRequests().catch((e) => {
        console.error("[Matches] getRequests failed:", e);
        return [];
      });
      const clean = sanitizeRequests(data, "requests");
      setRequests({
        dating: clean.filter((r) => r.type === "dating"),
        friend: clean.filter((r) => r.type === "friend"),
      });
    } catch (err) {
      console.error("[Matches] loadRequests failed:", err);
    } finally {
      setRequestsLoading(false);
    }
  };

  const loadPhones = async () => {
    try {
      const res = await api.get("/whatsapp/contacts");
      const map = {};
      const payload = res?.data || {};
      for (const m of [
        ...(Array.isArray(payload.dating_matches) ? payload.dating_matches : []),
        ...(Array.isArray(payload.friend_matches) ? payload.friend_matches : []),
      ]) {
        if (m?.user_id && m?.phone_number) map[m.user_id] = m.phone_number;
      }
      for (const r of (Array.isArray(payload.pending_requests) ? payload.pending_requests : [])) {
        if (r?.user_id && r?.phone_number) map[r.user_id] = r.phone_number;
      }
      setPhones(map);
    } catch (err) {
      // non-fatal
    }
  };

  const handleRespond = async (requestId, type, action) => {
    try {
      await respondRequest(requestId, action);
      toast.success(`${action === "accept" ? "Accepted" : "Rejected"} ${type} request`);
      await Promise.all([loadRequests(), loadMatches()]);
      if (action === "accept") {
        await markNotificationsRead();
      }
    } catch {
      toast.error("Failed to respond");
    }
  };

  const handleUnmatch = async (matchId, displayName, type) => {
    const label = type === "friendship" ? "Unfriend" : "Unmatch";
    if (!window.confirm(`${label} ${displayName}? This cannot be undone.`)) return;
    try {
      await api.delete(`/discover/matches/${matchId}`);
      toast.success(`${label}d`);
      loadMatches();
    } catch {
      toast.error(`Failed to ${label.toLowerCase()}`);
    }
  };

  const handleViewProfile = async (matchId) => {
    try {
      const profile = await getMatchProfile(matchId);
      if (!profile || typeof profile !== "object") {
        console.error("[Matches] getMatchProfile returned invalid payload:", profile);
        toast.error("Could not load profile");
        return;
      }
      setSelectedProfile(profile);
    } catch {
      toast.error("Could not load profile");
    }
  };

  const openWhatsApp = (phone) => {
    const digits = String(phone || "").replace(/[^\d+]/g, "");
    if (!digits) return;
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(
      "Hi! I saw your profile on Haven and would love to connect."
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleRefresh = () => {
    setLoading(true);
    Promise.all([loadMatches(), loadRequests(), loadPhones()])
      .finally(() => setLoading(false));
    toast.info("Refreshing…");
  };

  if (activeChat && activeChat.match_id) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <Navbar />
        <main className="max-w-2xl mx-auto">
          <MatchChat
            match={activeChat}
            onBack={() => { setActiveChat(null); loadMatches(); }}
          />
        </main>
      </div>
    );
  }

  const totalRequests = requests.dating.length + requests.friend.length;

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="font-display text-2xl font-semibold">Connections</h1>
          <div className="flex gap-2">
            <button
              onClick={handleRefresh}
              className="p-2 rounded-full hover:bg-[#F5F3EE]"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5 text-[#6B6B70]" />
            </button>
            <button
              onClick={() => setShowRequests((s) => !s)}
              className="relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white border border-[#E7E5E0] hover:border-[#7C3AED]"
            >
              <UserPlus className="w-4 h-4" />
              Requests
              {totalRequests > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center">
                  {totalRequests}
                </span>
              )}
            </button>
          </div>
        </div>
        <p className="text-sm text-[#6B6B70] mb-6">All your matches — dating and friends</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
            <button onClick={handleRefresh} className="ml-2 underline">Retry</button>
          </div>
        )}

        {showRequests && (
          <div className="mb-6 space-y-3">
            <div className="px-4 py-3 bg-[#F5F3EE] rounded-xl border border-[#E7E5E0] flex items-center justify-between">
              <h3 className="font-semibold text-sm">Pending Requests</h3>
              <button onClick={() => setShowRequests(false)} className="text-xs text-[#6B6B70]">
                Hide
              </button>
            </div>

            {requestsLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-[#7C3AED]" />
              </div>
            ) : totalRequests === 0 ? (
              <div className="p-6 text-center text-sm text-[#6B6B70]">
                No pending requests
              </div>
            ) : (
              [...requests.dating, ...requests.friend].map((req) => {
                // Defensive guard — a single bad entry can't kill the panel.
                if (!req || !req.request_id) return null;
                const isDating = req.type === "dating";
                return (
                  <div
                    key={req.request_id}
                    className="bg-white border border-[#E7E5E0] rounded-2xl p-4 flex items-center gap-3"
                  >
                    <div className="w-14 h-14 rounded-full overflow-hidden bg-[#F5F3EE] flex-shrink-0">
                      {req.from_image ? (
                        <img
                          src={req.from_image}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-xl">
                          {isDating ? "❤️" : "👥"}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">
                        {req.from_name || "Someone"}
                      </div>
                      <div className={`text-xs mt-0.5 ${isDating ? "text-red-500" : "text-blue-500"}`}>
                        {isDating ? "Dating request" : "Friend request"}
                      </div>
                      {req.from_country && (
                        <div className="flex items-center gap-1 text-xs text-[#6B6B70] mt-1">
                          <MapPin className="w-3 h-3" />
                          {req.from_country}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleRespond(req.request_id, req.type, "accept")}
                        className="w-9 h-9 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600"
                        title="Accept"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRespond(req.request_id, req.type, "reject")}
                        className="w-9 h-9 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                        title="Reject"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#7C3AED]" />
          </div>
        ) : matches.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="w-16 h-16 text-red-300 mx-auto mb-4" />
            <p className="text-base text-gray-500 mb-2">No connections yet</p>
            <p className="text-sm text-gray-500 mb-6">Start swiping to find matches and friends!</p>
            <Link
              to="/find"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#7C3AED] text-white rounded-full text-sm font-medium"
            >
              <Users className="w-4 h-4" /> Discover People
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {matches.map((m) => {
              // Defensive guard — protects against any entry that slipped
              // past the sanitizer (belt-and-braces so a single bad row
              // never takes down the whole list).
              if (!m || !m.match_id) return null;

              const isFriend = m.match_type === "friendship";
              const phone = phones[m.user_id];
              const profileImage = m.profile_image || "";

              return (
                <div
                  key={m.match_id}
                  className="bg-white border border-[#E7E5E0] rounded-xl p-3 flex items-center gap-3 hover:shadow-sm transition-shadow"
                >
                  <button
                    onClick={() => setActiveChat(m)}
                    className="relative flex-shrink-0"
                  >
                    <div className="w-14 h-14 rounded-full overflow-hidden border border-[#E7E5E0]">
                      {profileImage ? (
                        <img
                          src={profileImage}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-full h-full bg-[#F5F3EE] grid place-items-center">
                          {isFriend
                            ? <Users className="w-6 h-6 text-blue-400" />
                            : <Heart className="w-6 h-6 text-red-400" />}
                        </div>
                      )}
                    </div>
                    {m.unread_count > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center font-bold">
                        {m.unread_count}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => setActiveChat(m)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm truncate">
                        {m.display_name || "Anonymous"}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                        isFriend ? "bg-blue-50 text-blue-600" : "bg-red-50 text-red-600"
                      }`}>
                        {isFriend ? "Friend" : "Date"}
                      </span>
                    </div>
                    {m.last_message && (
                      <div className="text-xs text-[#6B6B70] truncate mt-0.5">
                        {m.last_message}
                      </div>
                    )}
                    {m.country && (
                      <div className="text-xs text-[#6B6B70] truncate mt-0.5">
                        {m.city ? `${m.city}, ` : ""}{m.country}
                      </div>
                    )}
                  </button>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {phone ? (
                      <button
                        onClick={() => openWhatsApp(phone)}
                        className="w-9 h-9 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:bg-[#1EBE5A]"
                        title="Open WhatsApp"
                      >
                        <Phone className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        disabled
                        className="w-9 h-9 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center"
                        title="No WhatsApp number"
                      >
                        <Phone className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleViewProfile(m.match_id)}
                      className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
                      title="View profile"
                    >
                      <Info className="w-4 h-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => setActiveChat(m)}
                      className="w-9 h-9 rounded-full hover:bg-purple-50 flex items-center justify-center"
                      title="Open chat"
                    >
                      <MessageCircle className="w-4 h-4 text-[#7C3AED]" />
                    </button>
                    <button
                      onClick={() => handleUnmatch(m.match_id, m.display_name, m.match_type)}
                      className="w-9 h-9 rounded-full hover:bg-red-50 flex items-center justify-center"
                      title={isFriend ? "Unfriend" : "Unmatch"}
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {unreadCount > 0 && (
          <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between text-sm">
            <span className="text-yellow-800">
              {unreadCount} unread message{unreadCount !== 1 ? "s" : ""}
            </span>
            <button
              onClick={async () => {
                await markNotificationsRead();
                setUnreadCount(0);
                toast.success("Marked as read");
              }}
              className="text-xs text-yellow-800 underline"
            >
              Mark all read
            </button>
          </div>
        )}
      </main>

      {selectedProfile && (
        <ProfileModal
          profile={selectedProfile}
          onClose={() => setSelectedProfile(null)}
        />
      )}
    </div>
  );
}